import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '../../firebase';
import {
  LEAGUES,
  InfoLeagueProps,
  SeasonProps,
  AsyncLeagueProps,
  CashbackBonusType,
} from '../../leagues';
import { parseCashback } from '../../utils';
import { useLeagueContext } from '../../context';

type RouteParams = {
  path?: string;
};

interface SeasonBonusProps {
  is2x1: boolean;
  cashback: {
    has: boolean;
    min: number;
    max: number;
  };
}

interface FailedProps {
  status: boolean;
  message: string;
}

export type UseLeagueResult = {
  asyncLeague: AsyncLeagueProps | null;
  league: InfoLeagueProps | null;
  currentSeason: SeasonProps | undefined;
  bonus: SeasonBonusProps;
  loading: boolean;
  failed: FailedProps;
};

const MAX_RETRIES = 7;
const FAILED_MESSAGE =
  'No se pudo obtener la información en este momento. Intenta nuevamente en unos instantes.';

export const useGetLeague = (
  leagueParam?: InfoLeagueProps,
): UseLeagueResult => {
  const { path } = useParams<RouteParams>();

  const [asyncLeague, setAsyncLeague] = useState<AsyncLeagueProps | null>(null);
  const [currentSeason, setCurrentSeason] = useState<SeasonProps | undefined>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<FailedProps>({
    status: false,
    message: '',
  });

  const league = useMemo(() => {
    if (leagueParam) return leagueParam;
    return LEAGUES.find((l) => l.path.slice(1) === path) ?? null;
  }, [leagueParam, path]);

  useEffect(() => {
    if (!league) {
      setAsyncLeague(null);
      setCurrentSeason(undefined);
      setFailed({
        status: false,
        message: '',
      });
      setLoading(false);
      return;
    }

    let cancelled = false;

    let unsubscribeLeague: (() => void) | null = null;
    let unsubscribeSeason: (() => void) | null = null;

    let leagueRetryTimeout: ReturnType<typeof setTimeout> | null = null;
    let seasonRetryTimeout: ReturnType<typeof setTimeout> | null = null;

    let leagueAttempt = 0;
    let seasonAttempt = 0;

    setLoading(true);
    setFailed({
      status: false,
      message: '',
    });

    const fail = () => {
      if (cancelled) return;

      if (unsubscribeLeague) unsubscribeLeague();
      if (unsubscribeSeason) unsubscribeSeason();

      setFailed({
        status: true,
        message: FAILED_MESSAGE,
      });

      setLoading(false);
    };

    const clearSeasonSubscription = () => {
      if (unsubscribeSeason) {
        unsubscribeSeason();
        unsubscribeSeason = null;
      }

      if (seasonRetryTimeout) {
        clearTimeout(seasonRetryTimeout);
        seasonRetryTimeout = null;
      }
    };

    const subscribeSeason = (seasonId: string) => {
      if (cancelled) return;

      clearSeasonSubscription();

      const seasonRef = doc(db, `leagues/${league.id}/seasons/${seasonId}`);

      unsubscribeSeason = onSnapshot(
        seasonRef,
        (seasonSnap) => {
          if (cancelled) return;

          if (!seasonSnap.exists()) {
            if (seasonAttempt >= MAX_RETRIES) {
              fail();
              return;
            }

            const delay = 1000 * 2 ** seasonAttempt;
            seasonAttempt += 1;

            clearSeasonSubscription();

            seasonRetryTimeout = setTimeout(() => {
              subscribeSeason(seasonId);
            }, delay);

            return;
          }

          seasonAttempt = 0;

          setCurrentSeason(seasonSnap.data() as SeasonProps);
          setLoading(false);
        },
        () => {
          if (cancelled) return;

          if (seasonAttempt >= MAX_RETRIES) {
            fail();
            return;
          }

          const delay = 1000 * 2 ** seasonAttempt;
          seasonAttempt += 1;

          clearSeasonSubscription();

          seasonRetryTimeout = setTimeout(() => {
            subscribeSeason(seasonId);
          }, delay);
        },
      );
    };

    const subscribeLeague = () => {
      if (cancelled) return;

      if (unsubscribeLeague) {
        unsubscribeLeague();
        unsubscribeLeague = null;
      }

      if (leagueRetryTimeout) {
        clearTimeout(leagueRetryTimeout);
        leagueRetryTimeout = null;
      }

      const leagueRef = doc(db, 'leagues', league.id);

      unsubscribeLeague = onSnapshot(
        leagueRef,
        (snap) => {
          if (cancelled) return;

          if (!snap.exists()) {
            if (leagueAttempt >= MAX_RETRIES) {
              fail();
              return;
            }

            const delay = 1000 * 2 ** leagueAttempt;
            leagueAttempt += 1;

            if (unsubscribeLeague) {
              unsubscribeLeague();
              unsubscribeLeague = null;
            }

            leagueRetryTimeout = setTimeout(() => {
              subscribeLeague();
            }, delay);

            return;
          }

          leagueAttempt = 0;

          const data = snap.data() as AsyncLeagueProps;
          setAsyncLeague(data);

          const seasonId = data.currentSeasonId;

          if (!seasonId) {
            if (leagueAttempt >= MAX_RETRIES) {
              fail();
              return;
            }

            const delay = 1000 * 2 ** leagueAttempt;
            leagueAttempt += 1;

            if (unsubscribeLeague) {
              unsubscribeLeague();
              unsubscribeLeague = null;
            }

            leagueRetryTimeout = setTimeout(() => {
              subscribeLeague();
            }, delay);

            return;
          }

          subscribeSeason(seasonId);
        },
        () => {
          if (cancelled) return;

          if (leagueAttempt >= MAX_RETRIES) {
            fail();
            return;
          }

          const delay = 1000 * 2 ** leagueAttempt;
          leagueAttempt += 1;

          if (unsubscribeLeague) {
            unsubscribeLeague();
            unsubscribeLeague = null;
          }

          leagueRetryTimeout = setTimeout(() => {
            subscribeLeague();
          }, delay);
        },
      );
    };

    subscribeLeague();

    return () => {
      cancelled = true;

      if (unsubscribeLeague) unsubscribeLeague();
      if (unsubscribeSeason) unsubscribeSeason();

      if (leagueRetryTimeout) clearTimeout(leagueRetryTimeout);
      if (seasonRetryTimeout) clearTimeout(seasonRetryTimeout);
    };
  }, [league]);

  const bonus = useMemo<SeasonBonusProps>(() => {
    if (!currentSeason)
      return {
        is2x1: false,
        cashback: {
          has: false,
          min: 0,
          max: 0,
        },
      };

    const bonusCashback = currentSeason.bonus.find(
      (b): b is CashbackBonusType => b.startsWith('cashback-'),
    );

    return {
      is2x1: currentSeason.bonus.includes('2x1') ?? false,
      cashback: parseCashback(bonusCashback),
    };
  }, [currentSeason]);

  return {
    asyncLeague,
    league,
    currentSeason,
    bonus,
    loading,
    failed,
  };
};

export const useLeague = () => {
  const league = useLeagueContext();

  return { ...league };
};
